import { HUB_LISTING_HORIZON_DAYS, eventCategoryForType } from "./constants.js";
import {
  formatHubListingContact,
  formatHubListingContactLabel,
  hubListingPhoneFromContact,
  hubListingPhoneLooksValid,
} from "./format.js";

const DOCUMENTATION_EXAMPLE_HOSTS = new Set(["example.com", "example.org", "example.net"]);

/** RFC 2606 documentation domains are not real destinations — never link to them. */
export function sanitizeListingUrl(url) {
  const text = String(url || "").trim();
  if (!text) return "";
  try {
    const host = new URL(text).hostname.replace(/^www\./i, "").toLowerCase();
    if (DOCUMENTATION_EXAMPLE_HOSTS.has(host)) return "";
  } catch {
    return "";
  }
  return text;
}

/** Curated in-system sources. These are not live scrapes of outside calendars. */
export const EVENT_SOURCES = [
  {
    id: "chamber-downtown",
    name: "San Diego Regional Chamber of Commerce",
    room: "chambers",
    url: "https://sdchamber.org/",
    events_url: "https://sdchamber.org/events/",
    address: "402 W. Broadway, Suite 1000, San Diego, CA 92101",
    contact: "Chamber events",
    phone: "619-544-1300",
  },
  {
    id: "city-civic",
    name: "City of San Diego community calendar",
    room: "city",
    url: "https://www.sandiego.gov/",
    events_url: "https://www.sandiego.gov/events",
    address: "202 C St, San Diego, CA 92101",
    contact: "City of San Diego",
    phone: "619-236-5555",
  },
  {
    id: "county-civic",
    name: "County of San Diego civic events",
    room: "county",
    url: "https://www.sandiegocounty.gov/",
    events_url: "https://www.sandiegocounty.gov/content/sdc/news/events.html",
    address: "1600 Pacific Hwy, San Diego, CA 92101",
    contact: "County of San Diego",
    phone: "858-694-3900",
  },
  {
    id: "state-assoc",
    name: "California Association of REALTORS®",
    room: "state",
    url: "https://www.car.org/",
    events_url: "https://www.car.org/events",
    address: "915 L Street, Suite 1460, Sacramento, CA 95814",
    contact: "Customer Contact Center",
    phone: "(213) 739-8227",
  },
  {
    id: "nat-assoc",
    name: "National Association of REALTORS®",
    room: "national",
    url: "https://www.nar.realtor/",
    events_url: "https://www.nar.realtor/events",
    address: "500 New Jersey Ave NW, Washington, DC 20001",
    contact: "Member support",
    phone: "800-874-6500",
  },
  {
    id: "conv-center",
    name: "San Diego Convention Center",
    room: "gatherings",
    url: "https://www.sdccc.org/",
    events_url: "https://www.sdccc.org/events",
    address: "111 W Harbor Dr, San Diego, CA 92101",
    contact: "Convention services",
    phone: "619-525-5000",
  },
  {
    id: "hotel-cal",
    name: "Conference hotel calendars",
    room: "gatherings",
    url: "https://www.sdccc.org/plan-your-event",
    events_url: "",
    address: "111 W Harbor Dr, San Diego, CA 92101",
    contact: "SDCCC event planning",
    phone: "619-525-5000",
  },
  {
    id: "re-conf",
    name: "Real estate conference circuit",
    room: "gatherings",
    url: "https://www.nar.realtor/",
    events_url: "https://www.nar.realtor/events",
    address: "800 W Katella Ave, Anaheim, CA 92802",
    contact: "Anaheim Convention Center",
    phone: "714-765-8950",
  },
  {
    id: "pm-events",
    name: "Property management gatherings",
    room: "gatherings",
    url: "https://www.irem.org/",
    events_url: "https://www.irem.org/events",
    address: "500 N Michigan Ave, Suite 900, Chicago, IL 60611",
    contact: "IREM member services",
    phone: "312-329-6000",
  },
  {
    id: "biz-council",
    name: "San Diego Regional Chamber — business councils",
    room: "chambers",
    url: "https://sdchamber.org/",
    events_url: "https://sdchamber.org/events/",
    address: "402 W. Broadway, Suite 1000, San Diego, CA 92101",
    contact: "Council programs",
    phone: "619-544-1300",
  },
  {
    id: "realtor-board",
    name: "Pacific Southwest Association of REALTORS® (PSAR)",
    room: "city",
    url: "https://www.psar.org/",
    events_url: "https://www.psar.org/calendar",
    address: "4340 Genesee Ave, Suite 203, San Diego, CA 92117",
    contact: "PSAR support",
    phone: "619-421-7811",
  },
];

export function sourceMeta(sourceId) {
  return EVENT_SOURCES.find((row) => row.id === sourceId) || null;
}

/** Title link priority: specific event → host events calendar → organization site. */
export function resolveListingTitleLink(item) {
  const eventUrl = sanitizeListingUrl(item?.url);
  if (eventUrl) return { href: eventUrl, kind: "event" };
  const source = sourceMeta(item?.source_id);
  const calendarUrl = sanitizeListingUrl(source?.events_url);
  if (calendarUrl) return { href: calendarUrl, kind: "calendar" };
  const orgUrl = sanitizeListingUrl(source?.url);
  if (orgUrl) return { href: orgUrl, kind: "org" };
  return { href: "", kind: "none" };
}

export const LISTING_TITLE_LINK_HINTS = {
  event: "Opens the event listing on the host site.",
  calendar: "Opens the host events calendar — confirm this listing there.",
  org: "Opens the host organization site.",
};

function catalogEvent(id, title, type, offsetDays, time, location, description, sourceId, extra = {}) {
  const source = sourceMeta(sourceId);
  return {
    id,
    title,
    type,
    category: extra.category || eventCategoryForType(type),
    date: "",
    roll_offset: offsetDays,
    rolling: true,
    time,
    location,
    address: extra.address || source?.address || "",
    contact: extra.contact || source?.contact || "",
    phone: extra.phone || source?.phone || "",
    description,
    url: sanitizeListingUrl(extra.url || ""),
    host: extra.host || source?.name || "",
    source_id: sourceId,
    thumbnail: extra.thumbnail || "",
    special: Boolean(extra.special),
    featured: Boolean(extra.featured),
    curated: true,
    group_id: extra.group_id || null,
    created_at: "2026-08-31T12:00:00.000Z",
    hidden: false,
  };
}

export const FEED_CATALOG = [
  catalogEvent(
    "th-feed-chamber-2",
    "Eastside chamber lunch",
    "chamber",
    4,
    "12:00 PM",
    "Eastside chamber dining room",
    "A midweek chamber lunch. Seating is limited; please confirm with the host.",
    "chamber-downtown"
  ),
  catalogEvent(
    "th-feed-city-2",
    "Harbor district open house week",
    "city",
    6,
    "10:00 AM",
    "Harbor civic plaza",
    "City-listed neighborhood hours. DreamHome is not a marketplace booth.",
    "city-civic"
  ),
  catalogEvent(
    "th-feed-county-2",
    "County permit workshop",
    "county",
    11,
    "2:00 PM",
    "County operations hall",
    "A county education hour for members who hold listings in unincorporated areas.",
    "county-civic"
  ),
  catalogEvent(
    "th-feed-state-2",
    "State ethics refresher",
    "state",
    18,
    "9:00 AM",
    "State association classroom",
    "State association education. Confirm hours on the host site.",
    "state-assoc"
  ),
  catalogEvent(
    "th-feed-nat-2",
    "National advocacy briefing",
    "national",
    22,
    "11:00 AM",
    "National conference hotel",
    "A national briefing listed for members. Dates may shift.",
    "nat-assoc"
  ),
  catalogEvent(
    "th-feed-conv-2",
    "Convention center housing day",
    "convention",
    15,
    "8:00 AM",
    "Convention center hall B",
    "Convention-center listing. Not hosted by REChecklist.",
    "conv-center"
  ),
  catalogEvent(
    "th-feed-hotel-2",
    "Waterfront hotel broker breakfast",
    "gathering",
    8,
    "7:45 AM",
    "Waterfront conference hotel",
    "Hotel calendar listing for a broker breakfast.",
    "hotel-cal"
  ),
  catalogEvent(
    "th-feed-conf-2",
    "Coastal conference welcome hour",
    "convention",
    27,
    "5:00 PM",
    "Coastal conference foyer",
    "Conference circuit listing. Verify the host program before you travel.",
    "re-conf"
  ),
  catalogEvent(
    "th-feed-pm-2",
    "Property managers roundtable",
    "summit",
    13,
    "3:30 PM",
    "Midtown education room",
    "A property-management gathering listed for members who hold both hats.",
    "pm-events"
  ),
  catalogEvent(
    "th-feed-council-2",
    "Business council twilight",
    "chamber",
    9,
    "5:30 PM",
    "Council terrace",
    "Business council calendar. Guests welcome with a member.",
    "biz-council"
  ),
  catalogEvent(
    "th-feed-board-2",
    "Realtor board orientation",
    "city",
    5,
    "9:15 AM",
    "Board education wing",
    "Local board listing. Confirm the room number with the board office.",
    "realtor-board"
  ),
];

export function feedDayStamp(date = new Date()) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

export function addDays(stamp, days) {
  const [year, month, day] = String(stamp).split("-").map(Number);
  const next = new Date(year, (month || 1) - 1, (day || 1) + Number(days || 0));
  return feedDayStamp(next);
}

export function hubListingHorizon(today = feedDayStamp()) {
  return addDays(today, HUB_LISTING_HORIZON_DAYS);
}

export function clampRollOffset(offset) {
  const n = Number(offset);
  if (!Number.isFinite(n)) return 7;
  return Math.min(Math.max(0, n), HUB_LISTING_HORIZON_DAYS);
}

/** Drop past listings and keep dates within the forward horizon. Rolling rows get a new date. */
export function reconcileListingRow(row, today) {
  const horizon = hubListingHorizon(today);
  const rollOffset = clampRollOffset(row.roll_offset);
  const rolls = Boolean(row.rolling || (row.curated && row.roll_offset != null && !row.group_id));
  let date = row.date || "";

  if (rolls) {
    if (!date || date < today || date > horizon) {
      date = addDays(today, rollOffset);
    }
  } else if (date) {
    if (date < today || date > horizon) return null;
  }

  if (!date || date < today || date > horizon) return null;
  return { ...row, date, roll_offset: row.roll_offset ?? rollOffset, rolling: rolls };
}

/** Fill url / address / contact from catalog templates and source defaults. */
export function enrichListingFields(row, templatesById = null) {
  if (!row) return row;
  const template = templatesById?.get(row.id);
  const source = sourceMeta(row.source_id || template?.source_id);
  const pick = (value, ...fallbacks) => {
    for (const item of [value, ...fallbacks]) {
      const text = String(item || "").trim();
      if (text) return text;
    }
    return "";
  };
  const pickUrl = (...candidates) => {
    for (const item of candidates) {
      const url = sanitizeListingUrl(item);
      if (url) return url;
    }
    return "";
  };
  const pickPhone = (...candidates) => {
    for (const item of candidates) {
      const text = String(item || "").trim();
      if (hubListingPhoneLooksValid(text)) return text;
    }
    for (const item of candidates) {
      const fromContact = hubListingPhoneFromContact(item);
      if (fromContact) return fromContact;
    }
    return "";
  };
  const pickContactLabel = (...candidates) => {
    for (const item of candidates) {
      const label = formatHubListingContactLabel(item);
      if (label) return label;
    }
    return "";
  };
  const phone = pickPhone(row.phone, template?.phone, source?.phone, row.contact, template?.contact, source?.contact);
  const contact = pickContactLabel(row.contact, template?.contact, source?.contact);
  return {
    ...row,
    source_id: pick(row.source_id, template?.source_id),
    host: pick(row.host, template?.host, source?.name),
    url: pickUrl(row.url, template?.url),
    address: pick(row.address, template?.address, source?.address),
    phone,
    contact,
  };
}

export function materializeCatalogRow(row, today) {
  const offset = clampRollOffset(row.roll_offset || 7);
  const date = row.date && !row.rolling ? row.date : addDays(today, offset);
  return reconcileListingRow({ ...row, roll_offset: offset, date }, today);
}

export function sourceLabel(sourceId) {
  return EVENT_SOURCES.find((row) => row.id === sourceId)?.name || "";
}
